import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import EnquiryForm from "@/components/EnquiryForm";
import { SITE, waLink } from "@/lib/site";
import { MANIFESTO_IMAGE } from "@/lib/data";

export default function Contact() {
  usePageMeta({
    title: "Contact TripTrix Voyages — Net Rates for Travel Agents",
    description:
      "Reach the TripTrix trade desk — net rates for North East India, Bhutan, Nepal & Darjeeling. Enquire, WhatsApp or call; we reply within 24 hours.",
    keywords:
      "contact North East India DMC, net rates enquiry for travel agents, TripTrix Voyages trade desk, DMC in Siliguri contact, get net rates North East India packages, travel agent registration North East India DMC, WhatsApp North East India tour operator",
  });
  return (
    <>
      <PageHeader
        overline="Trade desk"
        title="Talk to our North East India trade desk"
        sub="Send us your dates, pax and destinations and we'll come back within 24 hours with net rates and a workable itinerary — or reach us straight away on WhatsApp."
        image={MANIFESTO_IMAGE}
      />

      <section className="pb-28" data-testid="contact-section">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5 space-y-6">
            <Reveal>
              <div className="border border-[var(--tx-border)] bg-[var(--tx-bg-2)] p-8">
                <h3 className="font-display text-2xl font-light mb-6">Talk to our trade desk</h3>
                <ul className="space-y-5">
                  <li>
                    <a href={`tel:${SITE.phoneRaw}`} className="flex items-start gap-4 group">
                      <span className="grid h-11 w-11 place-items-center rounded-full border border-[var(--tx-border)] text-[var(--tx-gold)] group-hover:border-[var(--tx-gold)] transition-colors duration-300"><Phone className="h-4 w-4" /></span>
                      <span>
                        <span className="block text-xs uppercase tracking-[0.18em] text-[var(--tx-text-2)]">Call us</span>
                        <span className="text-lg group-hover:text-[var(--tx-gold)] transition-colors duration-300">{SITE.phoneDisplay}</span>
                      </span>
                    </a>
                  </li>
                  <li>
                    <a href={`mailto:${SITE.email}`} className="flex items-start gap-4 group">
                      <span className="grid h-11 w-11 place-items-center rounded-full border border-[var(--tx-border)] text-[var(--tx-gold)] group-hover:border-[var(--tx-gold)] transition-colors duration-300"><Mail className="h-4 w-4" /></span>
                      <span>
                        <span className="block text-xs uppercase tracking-[0.18em] text-[var(--tx-text-2)]">Email</span>
                        <span className="text-lg group-hover:text-[var(--tx-gold)] transition-colors duration-300">{SITE.email}</span>
                      </span>
                    </a>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="grid h-11 w-11 place-items-center rounded-full border border-[var(--tx-border)] text-[var(--tx-gold)]"><MapPin className="h-4 w-4" /></span>
                    <span>
                      <span className="block text-xs uppercase tracking-[0.18em] text-[var(--tx-text-2)]">Our offices</span>
                      {SITE.offices.map((o) => (
                        <span key={o.city} className="block mt-1">
                          <span className="text-[var(--tx-gold)] text-sm">{o.label} — {o.city}</span>
                          <span className="block text-sm text-[var(--tx-text)]/80">{o.lines.join(", ")}</span>
                        </span>
                      ))}
                    </span>
                  </li>
                </ul>
                <a href={waLink()} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-medium text-white hover:opacity-90 transition-opacity duration-300">
                  <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="border border-[var(--tx-border)] bg-[var(--tx-bg-2)] p-8 md:p-10">
                <h3 className="font-display text-2xl font-light mb-6">Request your itinerary</h3>
                <EnquiryForm />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
